import { Button } from '@app/components/various/Button';
import { WarningOutline } from '@app/components/various/Icons';
import { StickyModalFooter } from '@app/components/various/Modal';
import { Notification } from '@app/components/various/Notification';
import {
  createSelection,
  getBuyerIdFromCurrentOrder,
  getIsSeller,
  getSelectionImportErrors,
  getWasSelectionClosedDuringImport,
  getWasSelectionRemovedDuringImport,
  resetSelectionImport,
} from '@app/ducks';
import { compiledPaths } from '@app/paths';
import { ORDER_QUERY_KEY } from '@app/services/hooks/orders/useOrderDetails';
import { useModalVisibility } from '@app/utils/hooks';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

export const SelectionImportFailure = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'orders', 'selections']);
  const { hideModal } = useModalVisibility('SelectionImportModal');
  const queryClient = useQueryClient();

  const buyerId = useSelector(getBuyerIdFromCurrentOrder);
  const isSeller = useSelector(getIsSeller);
  const errors = useSelector(getSelectionImportErrors);
  const wasSelectionClosed = useSelector(getWasSelectionClosedDuringImport);
  const wasSelectionRemoved = useSelector(getWasSelectionRemovedDuringImport);

  const handleTryAgain = React.useCallback(() => {
    dispatch(resetSelectionImport());
  }, [dispatch]);

  const handleSeeAllSelections = React.useCallback(() => {
    navigate(compiledPaths.SELECTIONS({}));
  }, [navigate]);

  const handleCreateNewSelection = React.useCallback(() => {
    const buyerKey = isSeller ? { buyer: buyerId } : {};

    dispatch(createSelection({ name: '', redirectPath: 'PRODUCTS_ORDER', ...buyerKey }));
  }, [buyerId, dispatch, isSeller]);

  const actions = React.useMemo(() => {
    if (wasSelectionClosed) {
      return [
        {
          label: t('orders:see_order'),
          onClick: () => {
            queryClient.invalidateQueries({ queryKey: [ORDER_QUERY_KEY] });
            hideModal();
          },
        },
        {
          label: t('selections:create_new_selection'),
          onClick: handleCreateNewSelection,
        },
      ] as const;
    }

    if (wasSelectionRemoved) {
      return [
        {
          label: t('selections:see_all_selections'),
          onClick: handleSeeAllSelections,
        },
        {
          label: t('selections:create_new_selection'),
          onClick: handleCreateNewSelection,
        },
      ] as const;
    }

    return [
      {
        label: t('common:keep_browsing'),
        onClick: hideModal,
      },
      {
        label: t('common:try_again'),
        onClick: handleTryAgain,
      },
    ] as const;
  }, [handleCreateNewSelection, handleSeeAllSelections, handleTryAgain, hideModal, t, wasSelectionClosed, wasSelectionRemoved]);

  const [secondaryAction, primaryAction] = actions;

  return (
    <div className="w-full">
      <div className="mt-2 flex flex-col items-center gap-1">
        <WarningOutline className="size-10 text-error-45" />
        <div className="body-2">{t('common:import_failed')}</div>
      </div>
      <div className="flex flex-col gap-3 whitespace-pre-wrap not-empty:mt-6">
        {errors.map((error, index) => (
          <Notification key={index} type="error" message={error} isBordered />
        ))}
      </div>
      <StickyModalFooter>
        <div className="flex justify-end gap-2">
          <Button color="secondary" variant="ghost" onClick={secondaryAction.onClick}>
            {secondaryAction.label}
          </Button>
          <Button onClick={primaryAction.onClick}>{primaryAction.label}</Button>
        </div>
      </StickyModalFooter>
    </div>
  );
};
